import React, { useState, useEffect } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  Row,
  Col,
  PreviewAltCard,
  Icon,
  Button,
  RSelect,
} from "../../../components/Component";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import moment from "moment";
import { Link } from "react-router-dom";

const MonthlyReportsPage = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [downloading, setDownloading] = useState(false);
  const [alertStats, setAlertStats] = useState({
    critical: 0,
    unread: 0,
    critical_over_sla: 0,
    critical_sla_minutes: 60,
  });
  const [criticalAlerts, setCriticalAlerts] = useState([]);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const months = [
    { value: 1, label: "Janvier" },
    { value: 2, label: "Février" },
    { value: 3, label: "Mars" },
    { value: 4, label: "Avril" },
    { value: 5, label: "Mai" },
    { value: 6, label: "Juin" },
    { value: 7, label: "Juillet" },
    { value: 8, label: "Août" },
    { value: 9, label: "Septembre" },
    { value: 10, label: "Octobre" },
    { value: 11, label: "Novembre" },
    { value: 12, label: "Décembre" },
  ];

  useEffect(() => {
    loadReport();
  }, [year, month]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const [monthlyData, alertsData, criticalAlertsData] = await Promise.all([
        api.getMonthlyReport({ year, month }),
        api.getAlertStats(),
        api.getAlerts({
          page: 1,
          per_page: 5,
          status: "unread",
          severity: "critical",
        }),
      ]);
      setReportData(monthlyData);
      setAlertStats(alertsData || {});
      setCriticalAlerts(Array.isArray(criticalAlertsData?.data) ? criticalAlertsData.data : []);
    } catch (error) {
      toast.error("Erreur lors du chargement du rapport");
      console.error("Error loading report:", error);
      setReportData(null);
      setCriticalAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const blob = await api.downloadMonthlyReportPDF({ year, month });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport-mensuel-${moment(`${year}-${month}-01`).format("MMMM-YYYY")}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Rapport PDF téléchargé avec succès");
    } catch (error) {
      toast.error("Erreur lors du téléchargement du PDF");
      console.error("Error downloading PDF:", error);
    } finally {
      setDownloading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return "A l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <React.Fragment>
      <Head title="Rapports Mensuels" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Rapports Mensuels</BlockTitle>
              <BlockDes className="text-soft">
                <p>Générez et consultez les rapports mensuels HSE</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <li>
                  <div className="form-control-wrap" style={{ minWidth: "150px", width: "150px", display: "block" }}>
                    <RSelect
                      options={years.map((y) => ({ value: y, label: y.toString() }))}
                      value={{ value: year, label: year.toString() }}
                      onChange={(option) => setYear(option.value)}
                      placeholder="Année"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minWidth: "150px",
                          width: "100%",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          maxWidth: "100%",
                          overflow: "visible",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          overflow: "visible",
                        }),
                      }}
                    />
                  </div>
                </li>
                <li>
                  <div className="form-control-wrap" style={{ minWidth: "280px", width: "280px", display: "block" }}>
                    <RSelect
                      options={months}
                      value={months.find((m) => m.value === month)}
                      onChange={(option) => setMonth(option.value)}
                      placeholder="Mois"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minWidth: "280px",
                          width: "100%",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          maxWidth: "100%",
                          overflow: "visible",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          overflow: "visible",
                        }),
                      }}
                    />
                  </div>
                </li>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {loading ? (
          <Block>
            <div className="text-center p-4">
              <Spinner color="primary" />
              <p className="mt-2 text-soft">Chargement du rapport...</p>
            </div>
          </Block>
        ) : reportData ? (
          <>
            {/* KPI officiels */}
            <Block>
              <Row className="g-gs">
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Total Observations</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">
                          {reportData.overview.total}
                        </span>
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">
                          {reportData.overview.positive} positives • {reportData.overview.negative}{" "}
                          négatives
                        </span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Taux de clôture</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{reportData.overview.closure_rate}%</span>
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">
                          {reportData.overview.closed} clôturées • {reportData.overview.in_progress}{" "}
                          en cours
                        </span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Temps moyen</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">
                          {reportData.overview.avg_processing_time_hours}h
                        </span>
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">Temps moyen de traitement</span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Période</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{reportData.period.label}</span>
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">
                          {moment(reportData.period.start_date).format("DD/MM/YYYY")} -{" "}
                          {moment(reportData.period.end_date).format("DD/MM/YYYY")}
                        </span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
              </Row>
            </Block>

            {/* Tendances officielles */}
            <Block>
              <Row className="g-gs">
                <Col lg="6">
                  <PreviewAltCard>
                    <div className="nk-block-head">
                      <h6 className="title">Tendance d&apos;activité</h6>
                    </div>
                    <div className="card-inner">
                      <div className="amount mb-2">
                        {reportData.overview.total_change_percent > 0 ? "+" : ""}
                        {reportData.overview.total_change_percent}%
                      </div>
                      <p className="text-soft mb-0">
                        Evolution du volume d&apos;observations par rapport au mois precedent.
                      </p>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col lg="6">
                  <PreviewAltCard>
                    <div className="nk-block-head">
                      <h6 className="title">Tendance de maitrise</h6>
                    </div>
                    <div className="card-inner">
                      <div className="amount mb-2">{reportData.overview.closure_rate}%</div>
                      <p className="text-soft mb-0">
                        Taux de cloture mensuel avec {reportData.overview.in_progress} observations encore ouvertes.
                      </p>
                    </div>
                  </PreviewAltCard>
                </Col>
              </Row>
            </Block>

            {/* Alertes critiques */}
            <Block>
              <PreviewAltCard className="border border-danger">
                <div className="nk-block-head">
                  <h6 className="title text-danger">Alertes critiques</h6>
                </div>
                <Row className="g-gs mb-2">
                  <Col md="4">
                    <div className="card-amount">
                      <span className="amount text-danger">{alertStats.critical || 0}</span>
                    </div>
                    <span className="text-soft">Critiques totales</span>
                  </Col>
                  <Col md="4">
                    <div className="card-amount">
                      <span className="amount text-warning">{alertStats.unread || 0}</span>
                    </div>
                    <span className="text-soft">Non lues</span>
                  </Col>
                  <Col md="4">
                    <div className="card-amount">
                      <span className="amount text-danger">{alertStats.critical_over_sla || 0}</span>
                    </div>
                    <span className="text-soft">SLA depasse ({alertStats.critical_sla_minutes || 60} min)</span>
                  </Col>
                </Row>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Alerte critique</th>
                        <th>Date</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {criticalAlerts.length > 0 ? (
                        criticalAlerts.map((alert) => (
                          <tr key={alert.id}>
                            <td>
                              <div className="fw-medium">{alert.title || "Alerte critique"}</div>
                              <div className="small text-soft">{alert.message || ""}</div>
                            </td>
                            <td className="text-soft">{formatTime(alert.created_at)}</td>
                            <td className="text-end">
                              <Button tag={Link} to="/alerts" size="sm" color="danger">
                                Traiter
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-soft py-3">
                            Aucune alerte critique non lue.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </PreviewAltCard>
            </Block>

            {/* Accès rapport officiel PDF */}
            <Block>
              <PreviewAltCard>
                <div className="nk-block-head">
                  <h6 className="title">Diffusion officielle</h6>
                </div>
                <p className="text-soft mb-3">
                  Exportez ce rapport mensuel officiel en PDF pour comité HSE, direction et audit.
                </p>
                <Button
                  color="primary"
                  onClick={handleDownloadPDF}
                  disabled={downloading || !reportData}
                >
                  {downloading ? (
                    <>
                      <Spinner size="sm" className="me-1" />
                      Téléchargement...
                    </>
                  ) : (
                    <>
                      <Icon name="download"></Icon>
                      <span>Télécharger PDF</span>
                    </>
                  )}
                </Button>
              </PreviewAltCard>
            </Block>
          </>
        ) : (
          <Block>
            <div className="text-center p-4">
              <span className="text-soft">Aucune donnée disponible pour cette période</span>
            </div>
          </Block>
        )}
      </Content>
    </React.Fragment>
  );
};

export default MonthlyReportsPage;

